import { useCallback, useEffect, useState } from 'react';
import {
  ArrowPathIcon,
  ExclamationTriangleIcon,
  LinkSlashIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import type { Locale, MissingAsset } from '@xphoto/shared';
import { MISSING_ASSETS_DEFAULT_LIMIT } from '@xphoto/shared';
import { useI18n } from '../i18n';
import { interpolate } from '../gallery/format';
import { formatBytes, formatDateTime } from '../viewer/format';
import { ApiError } from '../gallery/api';
import { softDeleteAssets } from '../trash/trash-api';
import { fetchMissingAssets } from './missing-assets-api';

/**
 * Section « Fichiers absents » de la console Admin de galerie (audit A5).
 * Modèle structurel : `TrashSection` / `FileErrorsSection` (self-contained, pagination,
 * sélection, états).
 *
 * CE QU'ELLE RÉSOUT : un média dont le fichier a disparu du disque porte
 * `assets.is_missing = true`, et TOUTES les vues de galerie masquent ce prédicat
 * inconditionnellement. La ligne (et ses vignettes) était donc invisible ET
 * indestructible — seule la réinitialisation complète de la bibliothèque l'éliminait.
 *
 * POURQUOI UNE TABLE, ET ICI PLUTÔT QUE DANS LA GALERIE :
 *  · le fichier n'existe PLUS → une grille de vignettes serait cassée par construction
 *    (chaque miniature en 404). On montre ce qui reste VRAI : le chemin, le type, la
 *    taille et la date connues au dernier scan ;
 *  · c'est de la maintenance, pas de la navigation — sa place est à côté d'« Erreurs de
 *    fichiers », pas dans un onglet de galerie de plus vu par tous les comptes ;
 *  · la console est DÉJÀ réservée à l'écriture, ce que l'exigence demande.
 *
 * PARCOURS : sélectionner → « Mettre à la corbeille » (réutilise
 * `POST /api/libraries/:id/assets/soft-delete`, qui ne filtre pas `is_missing`) → onglet
 * « Poubelle » pour la purge définitive, qui gère déjà `ENOENT` (le fichier est absent
 * par définition, seules la ligne DB et les vignettes restent à supprimer).
 */

type LoadStatus = 'idle' | 'loading' | 'error' | 'ready';

const PAGE_SIZE = MISSING_ASSETS_DEFAULT_LIMIT;

export function MissingFilesSection({
  libraryId,
  canWrite,
}: {
  libraryId: string | null;
  canWrite: boolean;
}): React.JSX.Element {
  const { t, locale } = useI18n();

  const [status, setStatus] = useState<LoadStatus>('idle');
  const [assets, setAssets] = useState<MissingAsset[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (libraryId === null) {
      setStatus('idle');
      return;
    }
    const controller = new AbortController();
    setStatus('loading');
    fetchMissingAssets(libraryId, PAGE_SIZE, offset)
      .then((page) => {
        if (controller.signal.aborted) return;
        setAssets(page.assets);
        setTotal(page.total);
        // Élague la sélection des ids absents de la page courante (comme la corbeille).
        setSelected((prev) => {
          const present = new Set(page.assets.map((a) => a.id));
          const next = new Set<string>();
          for (const id of prev) if (present.has(id)) next.add(id);
          return next;
        });
        setStatus('ready');
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setStatus('error');
        void err;
      });
    return () => controller.abort();
  }, [libraryId, offset, reloadKey]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  const toggle = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  /** Coche/décoche toute la page courante (jamais au-delà : la sélection reste explicite). */
  const toggleAll = useCallback(() => {
    setSelected((prev) =>
      prev.size === assets.length ? new Set() : new Set(assets.map((a) => a.id)),
    );
  }, [assets]);

  const moveToTrash = useCallback(async () => {
    if (libraryId === null || selected.size === 0 || busy) return;
    setBusy(true);
    setErrorMsg(null);
    setNotice(null);
    try {
      const res = await softDeleteAssets(libraryId, [...selected]);
      setSelected(new Set());
      setNotice(interpolate(t('admin.missingFiles.trashed'), { count: res.deleted }, locale));
      setOffset(0);
      reload();
    } catch (err: unknown) {
      setErrorMsg(
        err instanceof ApiError && err.status === 403
          ? t('admin.missingFiles.errorForbidden')
          : t('admin.missingFiles.error'),
      );
    } finally {
      setBusy(false);
    }
  }, [libraryId, selected, busy, t, reload]);

  const rangeFrom = total === 0 ? 0 : offset + 1;
  const rangeTo = Math.min(offset + assets.length, total);
  const canPrev = offset > 0;
  const canNext = offset + PAGE_SIZE < total;

  return (
    <section
      id="admin-missing-files"
      role="tabpanel"
      aria-labelledby="admin-missing-files-heading"
      className="flex flex-col gap-6"
    >
      <div className="flex flex-col gap-1">
        <h3
          id="admin-missing-files-heading"
          className="flex items-center gap-2 text-base font-semibold text-fg"
        >
          <LinkSlashIcon className="h-5 w-5" aria-hidden="true" />
          {t('admin.missingFiles.title')}
        </h3>
        <p className="text-sm text-muted">{t('admin.missingFiles.intro')}</p>
      </div>

      {libraryId === null ? (
        <p className="py-6 text-center text-muted">{t('admin.noLibrarySelected')}</p>
      ) : status === 'error' ? (
        <MissingLoadError onRetry={reload} />
      ) : status === 'ready' ? (
        <>
          <div className="flex flex-wrap items-center gap-3">
            {canWrite && (
              <button
                type="button"
                disabled={selected.size === 0 || busy}
                onClick={() => void moveToTrash()}
                className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm font-medium text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
              >
                <TrashIcon className="h-4 w-4" aria-hidden="true" />
                {interpolate(
                  t('admin.missingFiles.trashSelected'),
                  { count: selected.size },
                  locale,
                )}
              </button>
            )}
            <button
              type="button"
              onClick={reload}
              disabled={busy}
              className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm font-medium text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
            >
              <ArrowPathIcon className="h-4 w-4" aria-hidden="true" />
              {t('admin.missingFiles.refresh')}
            </button>
          </div>

          {/* Rappel du parcours : la corbeille n'est qu'une étape, la purge est dans
              l'onglet voisin. Jamais l'information par la couleur seule (icône + texte). */}
          <p className="inline-flex items-start gap-2 rounded-md bg-surface-2 px-3 py-2 text-sm text-fg">
            <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
            <span>{t('admin.missingFiles.hint')}</span>
          </p>

          {notice !== null && (
            <p role="status" className="text-sm text-fg">
              {notice}
            </p>
          )}
          {errorMsg !== null && (
            <p role="alert" className="inline-flex items-center gap-1 text-sm text-danger">
              <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
              {errorMsg}
            </p>
          )}

          <MissingTable
            assets={assets}
            selected={selected}
            onToggle={toggle}
            onToggleAll={toggleAll}
            locale={locale}
          />

          {total > 0 && (
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-muted" role="status">
                {interpolate(
                  t('admin.missingFiles.range'),
                  {
                    from: rangeFrom,
                    to: rangeTo,
                    total,
                  },
                  locale,
                )}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={!canPrev}
                  onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
                  className="rounded-md border border-border px-3 py-2 text-sm font-medium text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
                >
                  {t('admin.missingFiles.previous')}
                </button>
                <button
                  type="button"
                  disabled={!canNext}
                  onClick={() => setOffset((o) => o + PAGE_SIZE)}
                  className="rounded-md border border-border px-3 py-2 text-sm font-medium text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
                >
                  {t('admin.missingFiles.next')}
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <p role="status" className="py-8 text-center text-muted">
          {t('admin.loading')}
        </p>
      )}
    </section>
  );
}

/** Tableau des fichiers absents : case à cocher + chemin + taille + dernière vue. */
function MissingTable({
  assets,
  selected,
  onToggle,
  onToggleAll,
  locale,
}: {
  assets: readonly MissingAsset[];
  selected: ReadonlySet<string>;
  onToggle: (id: string) => void;
  onToggleAll: () => void;
  locale: Locale;
}): React.JSX.Element {
  const { t } = useI18n();
  if (assets.length === 0) {
    return <p className="py-8 text-center text-muted">{t('admin.missingFiles.empty')}</p>;
  }
  const allChecked = selected.size === assets.length;
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[36rem] border-collapse text-sm">
        <thead>
          <tr className="border-b border-border text-xs font-medium text-muted">
            <th scope="col" className="p-2 text-start">
              <label className="inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={allChecked}
                  onChange={onToggleAll}
                  className="h-4 w-4 rounded border-border-strong text-brand focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
                />
                <span className="sr-only">{t('admin.missingFiles.selectAll')}</span>
              </label>
            </th>
            <th scope="col" className="p-2 text-start">
              {t('admin.missingFiles.colPath')}
            </th>
            <th scope="col" className="p-2 text-start">
              {t('admin.missingFiles.colSize')}
            </th>
            <th scope="col" className="p-2 text-start">
              {t('admin.missingFiles.colSeenAt')}
            </th>
          </tr>
        </thead>
        <tbody>
          {assets.map((asset) => {
            const seen = asset.seenAt === null ? null : formatDateTime(asset.seenAt, locale);
            const checkId = `missing-check-${asset.id}`;
            return (
              <tr key={asset.id} className="border-b border-border">
                <td className="p-2 align-top">
                  <input
                    id={checkId}
                    type="checkbox"
                    checked={selected.has(asset.id)}
                    onChange={() => onToggle(asset.id)}
                    aria-label={interpolate(t('admin.missingFiles.selectOne'), {
                      name: asset.filename,
                    })}
                    className="h-4 w-4 rounded border-border-strong text-brand focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
                  />
                </td>
                <td className="p-2 align-top text-fg">
                  <span className="break-all">{asset.relPath}</span>
                </td>
                <td className="p-2 align-top text-muted">{formatBytes(asset.sizeBytes, locale)}</td>
                <td className="p-2 align-top text-muted">
                  {seen ?? t('admin.missingFiles.seenUnknown')}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** État d'erreur de chargement (icône + texte + réessai). */
function MissingLoadError({ onRetry }: { onRetry: () => void }): React.JSX.Element {
  const { t } = useI18n();
  return (
    <div className="flex flex-col items-center gap-3 py-8 text-center">
      <ExclamationTriangleIcon className="h-8 w-8 text-danger" aria-hidden="true" />
      <p className="text-fg">{t('admin.errorTitle')}</p>
      <button
        type="button"
        onClick={onRetry}
        className="rounded-md bg-brand px-3 py-2 text-sm font-medium text-brand-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
      >
        {t('admin.retry')}
      </button>
    </div>
  );
}
